import './styles/index.css';

import { NewVersionToast } from '@/components/layout/NewVersionToast';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/lib/auth';
import { BuildVersionProvider } from '@/lib/buildVersion';
import { queryClient } from '@/lib/queryClient';
import { AppRouter } from '@/router';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// 새 배포 후 옛 번들이 사라진 chunk hash 를 요청하면 dynamic import 실패가 발생한다.
// 사용자가 새로고침 안 해도 자동 1회 복구. sessionStorage 키로 무한 reload 루프 차단.
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|ChunkLoadError|Importing a module script failed|Loading chunk \d+ failed/i;
const RELOAD_LOCK_KEY = 'innermate.chunkReloadAt';
const RELOAD_LOCK_TTL_MS = 60_000;

function tryAutoReload(message: string) {
  if (!CHUNK_ERROR_RE.test(message)) return;
  const prev = sessionStorage.getItem(RELOAD_LOCK_KEY);
  if (prev) {
    const at = Number(prev);
    if (!Number.isNaN(at) && Date.now() - at < RELOAD_LOCK_TTL_MS) return;
  }
  sessionStorage.setItem(RELOAD_LOCK_KEY, String(Date.now()));
  location.reload();
}

window.addEventListener('error', (e) => {
  tryAutoReload(e.message ?? '');
});
window.addEventListener('unhandledrejection', (e) => {
  const msg = String((e.reason as { message?: unknown })?.message ?? e.reason ?? '');
  tryAutoReload(msg);
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root 엘리먼트를 찾을 수 없습니다');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <BuildVersionProvider>
            <AppRouter />
            <NewVersionToast />
            <Toaster />
          </BuildVersionProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
